import { Link } from 'react-router-dom';
import { etiquetaCliente, textoSinLlamadas } from '@/lib/cuadro/filtros';
import { diaMadrid } from '@/lib/llamadas/fechas';
import {
  ETIQUETA_ESTADO,
  derivadoA,
  estadoVisual,
  etiquetaMotivo,
  fmtDuracion,
  horaMadrid,
  type EstadoVisual,
} from '@/lib/llamadas/presentacion';
import type { LlamadaFeedRow } from '@/types/llamadas';

// Tabla histórica del periodo (prototipo: tbody), acotada a las 10 más
// recientes. "Ver" deep-linkea a la conversación en Atención (SPA).

// Solo el color: el texto del estado sale de `ETIQUETA_ESTADO`, el mismo que usa
// la exportación a Excel.
const BADGE: Record<EstadoVisual, { color: string; bg: string }> = {
  en_curso: { color: 'var(--status-live)', bg: 'var(--status-live-bg)' },
  procesando: { color: 'var(--text-2)', bg: 'var(--surface-3)' },
  resuelta: { color: 'var(--status-ok-ink)', bg: 'var(--status-ok-bg)' },
  escalada: { color: 'var(--status-esc-ink)', bg: 'var(--status-esc-bg)' },
};

function fechaCorta(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('es-ES', {
    timeZone: 'Europe/Madrid',
    day: '2-digit',
    month: '2-digit',
  }).format(new Date(iso));
}

export function TablaLlamadas({
  llamadas,
  periodoVacio,
}: {
  llamadas: LlamadaFeedRow[];
  periodoVacio: boolean;
}) {
  const filas = llamadas.slice(0, 10);
  if (filas.length === 0) {
    return (
      <p data-testid="tabla-vacia" className="text-[12.5px] text-[var(--text-dim)]">
        {textoSinLlamadas(periodoVacio)}.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-[var(--text-dim)]">
            <th className="px-2 py-2">Fecha</th>
            <th className="px-2 py-2">Cliente</th>
            <th className="px-2 py-2">Motivo</th>
            <th className="px-2 py-2">Estado</th>
            <th className="px-2 py-2">Derivado a</th>
            <th className="px-2 py-2 text-right">Duración</th>
            <th className="px-2 py-2 print:hidden" aria-label="Acciones" />
          </tr>
        </thead>
        <tbody>
          {filas.map((l) => {
            const estado = estadoVisual(l);
            const badge = BADGE[estado];
            const derivado = derivadoA(l) ?? '—';
            // Una pregunta, un criterio. «¿Hay cliente?» se contesta con `id_cliente`,
            // igual que en el Excel (`exportar.ts`), y el nombre lo pone SIEMPRE
            // `etiquetaCliente`. Antes esta tabla decidía por la veracidad de la razón
            // social, así que una llamada CON cliente y con la razón social vacía salía
            // con el teléfono en pantalla y «Sin identificar» en el papel, mientras el
            // Excel y el panel de clientes la nombraban con su id de Odoo: tres
            // respuestas distintas para la misma fila.
            const sinCliente = l.id_cliente === null;
            const quien = sinCliente ? l.telefono_origen || 'número oculto' : etiquetaCliente(l);
            return (
              <tr
                key={l.id}
                data-testid="fila-tabla"
                className="border-t border-[var(--border-soft)] hover:bg-[var(--surface-2)]"
              >
                <td className="mono whitespace-nowrap px-2 py-2 text-[var(--text-2)]">
                  {fechaCorta(l.inicio)} · {horaMadrid(l.inicio)}
                </td>
                <td className="max-w-[220px] truncate px-2 py-2 font-medium text-[var(--navy)]">
                  {sinCliente ? (
                    <>
                      {/* En pantalla, el número; en papel, no: el PDF sale de la
                          superficie que gobernamos, igual que el Excel (D7). */}
                      {/* Veracidad y no `??`, igual que en `quien`: `_norm_tel` deja el
                          teléfono en cadena vacía cuando llega `anonymous`, y con `??`
                          esta celda salía EN BLANCO mientras el enlace de la misma fila
                          decía «Ver la llamada de número oculto». */}
                      <span className="mono font-normal text-[var(--text-2)] print:hidden">
                        {l.telefono_origen || 'Número oculto'}
                      </span>
                      <span className="hidden font-normal text-[var(--text-2)] print:inline">
                        Sin identificar
                      </span>
                    </>
                  ) : (
                    etiquetaCliente(l)
                  )}
                </td>
                <td className="max-w-[180px] truncate px-2 py-2 text-[var(--text-2)]">
                  {/* Por veracidad y no con `!= null`: un motivo vacío («», que la
                      columna admite porque no tiene CHECK) es «sin motivo», igual que
                      un nulo. Es lo que hacen ya el Excel, los chips y la cabecera
                      impresa; con `!= null` esta celda se quedaba en blanco mientras el
                      fichero de la misma pantalla decía «Sin clasificar». */}
                  {l.motivo ? etiquetaMotivo(l.motivo) : '—'}
                </td>
                <td className="whitespace-nowrap px-2 py-2">
                  <span
                    className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    style={{ color: badge.color, background: badge.bg }}
                  >
                    {ETIQUETA_ESTADO[estado]}
                  </span>
                </td>
                <td className="whitespace-nowrap px-2 py-2 text-[var(--text-2)]">{derivado}</td>
                <td className="mono whitespace-nowrap px-2 py-2 text-right text-[var(--text-2)]">
                  {fmtDuracion(l.duracion_s)}
                </td>
                <td className="px-2 py-2 text-right print:hidden">
                  {l.inicio && (
                    <Link
                      to={`/atencion?call=${l.id}&dia=${diaMadrid(l.inicio)}`}
                      aria-label={`Ver la llamada de ${quien}`}
                      className="font-semibold text-[var(--orange-ink)] hover:underline"
                    >
                      Ver
                    </Link>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
